import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { CAP, useSession } from "@/auth/session";
import { Chip } from "@/components/Chip";
import { PageHead } from "@/components/PageHead";
import { SearchInput } from "@/components/inputs";
import { products, type PermGroupRow, type PermMapRow, type PermRow } from "@/api/client";
import { DataTable, type DataCol } from "@/components/DataTable";
import { Select } from "@/components/inputs";
import { qk, usePermissionMaps } from "@/modules/products/data";
import { ChoosePrompt, ProductSelect, ScopeBar, ScopeField, ScopeAction } from "@/modules/products/ScopeBar";
import { useKindParam } from "@/lib/hooks/useKindParam";

/** The permission vocabulary a product's tools use.
 *
 *  PER (PRODUCT, TOOL), not per product. D-89 recorded "the map goes to kind"
 *  and was incomplete: Cluster carries BOTH a `uds_tool` map (6 groups, 21
 *  permissions) and an `eol_tool` map (1 group, 5). The same integer means
 *  different things in different tools, which is exactly why nothing outside
 *  a product may reason about the numbers.
 *
 *  THE NUMBERS ARE THE CONTRACT. Every deployed bench compares against these
 *  integers, so a permission's id is NOT editable once it exists — changing
 *  what 207 means silently changes what every bench holding it may do, with
 *  no error anywhere, because the tool simply believes the number. Deprecate
 *  and add a new one; the page offers exactly those two actions.
 *
 *  SUPER ADMIN ONLY, per Dattatray. `CAP.MANAGE_PRODUCTS` is held by no plan
 *  and no company role, so a customer sees the vocabulary and no controls.
 */
export function PermissionMapPage() {
  const { can } = useSession();
  const mayAuthor = can(CAP.MANAGE_PRODUCTS);
  const [kind, setKind] = useKindParam();   // ?kind= arrives from the Products drawer
  const [tool, setTool] = useState("");
  const q = usePermissionMaps(kind);
  const [addMap, setAddMap] = useState(false);
  const [addGroupTo, setAddGroupTo] = useState<PermMapRow | null>(null);
  const [addPermTo, setAddPermTo] = useState<PermGroupRow | null>(null);

  return (
    <div className="mpage on">
      <PageHead
        eyebrow="Products"
        title="Tool permission map"
        desc="The vocabulary a product's tools use, per tool. The numbers are what a bench compares against, so they are never re-pointed — deprecate and add instead."
      />

      {/* SECOND SELECTOR IS THE TOOL, NOT A MODEL. Dattatray confirmed the
          key: "(product, TOOL) is correct. We decided that we can allocate
          Tool release to a specific model — I think that was the catch (its
          part of tool release rule)." So model targeting lives on RELEASES;
          a map belongs to a product and a tool. */}
      <ScopeBar right={mayAuthor ? (
        <button className={addMap ? "btn ghost sm" : "btn teal sm"}
          disabled={!kind} title={!kind ? "Choose a product first" : undefined}
          onClick={() => setAddMap(!addMap)}>
          {addMap ? "Cancel" : "Add a tool map"}
        </button>
      ) : undefined}>
        <ProductSelect value={kind} onChange={(k) => { setKind(k); setTool(""); }} />
        <ScopeField label="Tool"
          hint="the same number means different things in different tools">
          <Select value={tool} onChange={(e) => setTool(e.target.value)}
            disabled={!kind} style={{ minWidth: 200 }}>
            <option value="">All tools</option>
            {(q.data?.maps ?? []).map((m) => (
              <option key={m.map_id} value={m.tool}>{m.name}</option>
            ))}
          </Select>
        </ScopeField>
      </ScopeBar>

      {addMap && <AddMapRow kind={kind} onDone={() => setAddMap(false)} />}

      {!kind && <ChoosePrompt what="permission map" />}
      {kind && q.isLoading && <div className="panel panel-pad cap">Loading…</div>}
      {kind && !q.isLoading && (q.data?.maps.length ?? 0) === 0 && (
        <div className="panel panel-pad cap">
          No permission map for this product yet.
        </div>
      )}

      {/* ONE TABLE PER MAP — `DataTable`, the control every other list page
          in this module uses (Spaces, Tool updates, Products, Organisations).
          This page had been rendering its own three-level nest: a panel per
          map, a bold line per group, a hand-built row per permission with a
          `marginLeft: 14` indent. It was the only list in the console that
          did not look like a list, which is what Dattatray kept reporting as
          "no consistency with Orbit other subpages".

          The GROUP becomes a column rather than a nesting level. That loses
          nothing — a group is an attribute of a permission, not a container
          with its own behaviour — and it gains sorting, paging, alignment and
          the empty state for free. */}
      {(q.data?.maps ?? []).filter((m) => !tool || m.tool === tool).map((m) => (
        <div key={m.map_id}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
            <div className="section-label">{m.name}</div>
            <span className="mono cap">{m.tool}</span>
            <Chip tone="mute">{m.permission_count} permissions</Chip>
            {mayAuthor && (
              <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                <button className="btn ghost sm"
                  onClick={() => setAddGroupTo(addGroupTo?.map_id === m.map_id ? null : m)}>
                  {addGroupTo?.map_id === m.map_id ? "Cancel" : "Add group"}
                </button>
                <button className="btn ghost sm" disabled={!m.groups.length}
                  title={!m.groups.length ? "Add a group first - a permission lives in one" : undefined}
                  onClick={() => setAddPermTo(
                    addPermTo && m.groups.some((g) => g.group_uid === addPermTo.group_uid)
                      ? null : m.groups[0])}>
                  {addPermTo && m.groups.some((g) => g.group_uid === addPermTo.group_uid)
                    ? "Cancel" : "Add permission"}
                </button>
              </span>
            )}
          </div>

          {addGroupTo?.map_id === m.map_id && (
            <AddGroupRow kind={kind} map={m} onDone={() => setAddGroupTo(null)} />
          )}
          {addPermTo && m.groups.some((g) => g.group_uid === addPermTo.group_uid) && (
            <AddPermissionRow kind={kind} group={addPermTo}
              onDone={() => setAddPermTo(null)} />
          )}

          <DataTable
            rows={m.groups.flatMap((g) => g.permissions.map((perm) => ({
              ...perm, group_name: g.name, group_id: g.group_id,
              group_deprecated: g.deprecated,
            })))}
            cols={permCols(kind, mayAuthor)}
            rowKey={(r) => r.perm_uid ?? `${r.permission_id}`}
            empty={<div className="panel panel-pad cap">
              No permissions in this map yet.
            </div>} />
        </div>
      ))}

    </div>
  );
}

/** One flat row per permission — the group is a COLUMN, not a nesting level.
 *  A group is an attribute of a permission, not a container with behaviour of
 *  its own, so flattening loses nothing and gains sorting, alignment, paging
 *  and the empty state that `DataTable` already provides. */
type PermFlat = PermRow & {
  group_name: string; group_id: number; group_deprecated: boolean;
};

function permCols(kind: string, mayAuthor: boolean): DataCol<PermFlat>[] {
  return [
    { key: "group", label: "Group", width: 190,
      render: (r) => (
        <span style={{ display: "flex", gap: 6, alignItems: "baseline" }}>
          {r.group_name}
          {r.group_deprecated && <Chip tone="mute">deprecated</Chip>}
        </span>
      ) },
    { key: "gid", label: "Group no.", width: 90, mono: true, render: (r) => r.group_id },
    { key: "pid", label: "Number", width: 90, mono: true, render: (r) => r.permission_id },
    { key: "name", label: "Permission", width: 260,
      render: (r) => (
        <span style={{ textDecoration: r.deprecated ? "line-through" : undefined,
          color: r.deprecated ? "var(--t-dim)" : undefined }}>{r.name}</span>
      ) },
    { key: "state", label: "", width: 110,
      render: (r) => (r.deprecated ? <Chip tone="mute">deprecated</Chip> : <Chip tone="ok">live</Chip>) },
    ...(mayAuthor ? [{
      key: "act", label: "", width: 120,
      render: (r: PermFlat) => <PermissionAction kind={kind} perm={r} />,
    }] : []),
  ];
}

function PermissionAction({ kind, perm }: {
  kind: string; perm: PermRow;
}) {
  const qc = useQueryClient();
  const toggle = useMutation({
    mutationFn: () => products.updatePermission(kind, perm.perm_uid ?? "",
      { deprecated: !perm.deprecated }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.maps(kind) }),
  });
  if (!perm.perm_uid) return null;
  return (
    <button className="btn ghost sm"
      disabled={toggle.isPending} onClick={() => toggle.mutate()}>
      {perm.deprecated ? "Restore" : "Deprecate"}
    </button>
  );
}

/** The three authoring forms, all INLINE. Dattatray: "No Pop up."
 *
 *  A modal was wrong here for a specific reason, not just a style one: each
 *  of these adds something INTO a thing already on screen — a map into a
 *  product, a group into a map, a permission into a group. Covering that
 *  thing with a dialog hides the context the number has to be unique within,
 *  which is exactly what the operator needs to see while typing it.
 */
function InlineForm({ children, onCancel, onGo, go, busy, err }: {
  children: React.ReactNode; onCancel: () => void; onGo: () => void;
  go: string; busy: boolean; err: unknown;
}) {
  return (
    <div className="panel panel-pad" style={{
      display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap",
      marginBottom: 12,
    }}>
      {children}
      <ScopeAction>
        <span style={{ display: "flex", gap: 8 }}>
          <button className="btn teal sm" disabled={busy} onClick={onGo}>
            {busy ? "Saving…" : go}
          </button>
          <button className="btn ghost sm" onClick={onCancel}>Cancel</button>
        </span>
      </ScopeAction>
      {err ? (
        <div className="panel panel-pad" style={{ flexBasis: "100%" }}>
          {(err as Error).message}
        </div>
      ) : null}
    </div>
  );
}

function AddMapRow({ kind, onDone }: { kind: string; onDone: () => void }) {
  const qc = useQueryClient();
  const [tool, setTool] = useState("");
  const [name, setName] = useState("");
  const add = useMutation({
    mutationFn: () => products.addMap(kind, { tool, name: name || undefined }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.maps(kind) });
      void qc.invalidateQueries({ queryKey: qk.products });
      onDone();
    },
  });
  return (
    <InlineForm onCancel={onDone} onGo={() => add.mutate()} go="Add map"
      busy={!tool || add.isPending} err={add.error}>
      <ScopeField label="Tool slug" hint="what the tool identifies itself as">
        <SearchInput value={tool} autoFocus mono placeholder="uds_tool"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setTool(e.target.value.toLowerCase().replace(/\s+/g, "_"))} />
      </ScopeField>
      <ScopeField label="Name">
        <SearchInput value={name} placeholder="UDS Tool"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} />
      </ScopeField>
    </InlineForm>
  );
}

function AddGroupRow({ kind, map, onDone }: {
  kind: string; map: PermMapRow; onDone: () => void;
}) {
  const qc = useQueryClient();
  const [gid, setGid] = useState("");
  const [name, setName] = useState("");
  const add = useMutation({
    mutationFn: () => products.addGroup(kind, map.map_id, { group_id: Number(gid), name }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.maps(kind) });
      onDone();
    },
  });
  return (
    <InlineForm onCancel={onDone} onGo={() => add.mutate()} go="Add group"
      busy={!/^\d+$/.test(gid) || !name || add.isPending} err={add.error}>
      <ScopeField label="Group number" hint={`unique within ${map.tool}`}>
        <SearchInput value={gid} autoFocus mono placeholder="200" style={{ width: 100 }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGid(e.target.value)} />
      </ScopeField>
      <ScopeField label="Name">
        <SearchInput value={name} placeholder="Flashing"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} />
      </ScopeField>
    </InlineForm>
  );
}

function AddPermissionRow({ kind, group, onDone }: {
  kind: string; group: PermGroupRow; onDone: () => void;
}) {
  const qc = useQueryClient();
  const [pid, setPid] = useState("");
  const [name, setName] = useState("");
  const add = useMutation({
    mutationFn: () => products.addPermission(kind, group.group_uid ?? "",
      { permission_id: Number(pid), name }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.maps(kind) });
      onDone();
    },
  });
  return (
    <InlineForm onCancel={onDone} onGo={() => add.mutate()} go="Add permission"
      busy={!/^\d+$/.test(pid) || !name || add.isPending} err={add.error}>
      <ScopeField label="Permission number"
        hint="unique across the WHOLE map — a bench gets a flat set. Permanent.">
        <SearchInput value={pid} autoFocus mono placeholder="207" style={{ width: 100 }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPid(e.target.value)} />
      </ScopeField>
      <ScopeField label="Name">
        <SearchInput value={name} placeholder="Write DID"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} />
      </ScopeField>
    </InlineForm>
  );
}
